/**
 * @description Hook to sort expenses
 * @param {Expense[]} expenses - The expenses to sort
 * @param {function} handleSort - The function to handle the sort
 * @param {function} getSortIcon - The function to get the sort icon
 * @param {function} sortedExpenses - The function to get the sorted expenses
 * @param {function} sortState - The function to get the sort state
 * @param {function} getNextSortDirection - The function to get the next sort direction
 * @param {function} setSortState - The function to set the sort state
 * @returns {UseExpenseSortReturn} - The sorted expenses
 * @returns {SortState} - The sort state
 */

import React, { useState, useMemo, useCallback } from "react";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import type {
  Expense, SortState, SortableColumn, SortDirection,
} from "@/features/vouchers";

type UseExpenseSortProps = { expenses: Expense[] };

type UseExpenseSortReturn = {
  sortedExpenses: Expense[]
  sortState: SortState
  handleSort: (column: SortableColumn) => void
  getSortIcon: (column: SortableColumn) => React.ReactNode
};

const getNextSortDirection = (currentDirection: SortDirection): SortDirection => {
  switch (currentDirection) {
    case "none":
      return "asc";
    case "asc":
      return "desc";
    case "desc":
      return "none";
    default:
      return "asc";
  }
};

export const useExpenseSort = ({ expenses }: UseExpenseSortProps): UseExpenseSortReturn => {
  const [sortState, setSortState] = useState<SortState>({
    column: null,
    direction: "none",
  });

  const sortedExpenses = useMemo(() => {
    if (!sortState.column || sortState.direction === "none") {
      return expenses;
    }

    return [...expenses].sort((a, b) => {
      let comparison: number;

      switch (sortState.column) {
        case "date":
          comparison = new Date(a.authorization_time).getTime() - new Date(b.authorization_time).getTime();
          break;
        case "amount":
          comparison = a.amount - b.amount;
          break;
        case "merchant":
          comparison = a.merchant_name.localeCompare(b.merchant_name);
          break;
        default:
          return 0;
      }

      return sortState.direction === "asc" ? comparison : -comparison;
    });
  }, [expenses, sortState]);

  const handleSort = useCallback((column: SortableColumn) => {
    setSortState((prevState) => {
      const isSameColumn = prevState.column === column;
      const nextDirection = isSameColumn ? getNextSortDirection(prevState.direction) : "asc";

      return {
        column: nextDirection === "none" ? null : column,
        direction: nextDirection,
      };
    });
  }, []);

  const getSortIcon = useCallback((column: SortableColumn): React.ReactNode => {
    if (sortState.column !== column) {
      return React.createElement(ArrowUpDown, { size: 16 });
    }

    switch (sortState.direction) {
      case "asc":
        return React.createElement(ArrowUp, { size: 16 });
      case "desc":
        return React.createElement(ArrowDown, { size: 16 });
      default:
        return React.createElement(ArrowUpDown, { size: 16 });
    }
  }, [sortState]);

  return {
    sortedExpenses,
    sortState,
    handleSort,
    getSortIcon,
  };
};
